// When a therapist's current Service is EXPECTED to end — the "estimated
// availability" an Engaged therapist carries (CORE-01B §4.4; Canonical
// Terminology: "Engaged — busy but has an estimated future availability") and
// the "expected Service end" CORE-02 plans the next Dispatch against.
//
// ═══ WHY THIS EXISTS ═══
//
// A commitment used to be busy from `scheduled_at` for the service's length.
// For ASAP, `scheduled_at` is the moment the client BOOKED — but the Service
// cannot start until a rider has collected her, dropped her at the client, and
// the 15-minute Service Start countdown has run (TEND-040/041). So a 60-minute
// ASAP booked at 3:00 PM read as free at 4:00 PM while she was mid-massage, and
// ASAP could offer her a second booking.
//
// ═══ THE ORDER OF EVIDENCE — the best fact the booking row holds wins ═══
//
//   1. Service running → `service_ends_at`, the server's own timer. It already
//      moves with approved in-service changes (0157) and emergency-pause holds
//      (0178/0212), so an Extension is included without anything here.
//   2. Dropped off, countdown running → the countdown deadline (the server
//      auto-starts at zero, TEND-041) — or now, if the sweep is late — plus the
//      paid duration.
//   3. Not at the client yet and the start has already passed → now + the
//      countdown + the duration: the earliest she could possibly finish if she
//      arrived this instant. It rolls forward while she travels, so she never
//      reads as free before she can be. Every ASAP booking is here until
//      drop-off, because its `scheduled_at` is the moment it was booked.
//   4. Start still ahead → `scheduled_at` + duration: the confirmed window,
//      exactly as before.
//
// ⚠ NO BUFFER. This is when the Service ends, not when she is ready for the
// next one: no rest, preparation or post-service travel time is added, because
// the approved spec defines none (CORE-01B ranks Engaged therapists by travel
// AFTER availability, it does not add it). A rest period between bookings is a
// policy question for Luigi, not a default to add here.
//
// ⚠ Rule 3 can run up to the countdown EARLY: a therapist may start manually at
// drop-off (TEND-041), finishing 15 minutes sooner. That errs toward showing
// her free a little early; rule 2 and rule 1 correct it the moment she arrives.

/** TEND-040: Confirm Drop-Off starts a non-pausable 15-minute Service Start countdown (0106). */
export const SERVICE_START_COUNTDOWN_MINUTES = 15;

const MINUTE_MS = 60_000;

export interface ServiceTimingFacts {
  /** The booking's start: the booked moment for ASAP, the appointment for Scheduled. */
  scheduledAt: string;
  /** The paid Service length. */
  durationMinutes: number;
  /** Set at Confirm Drop-Off — when the Service Start countdown runs out (0106). */
  serviceStartDeadlineAt: string | null;
  /** Set when the Service starts; moves with in-service changes and emergency pauses. */
  serviceEndsAt: string | null;
}

function instant(iso: string, field: string): number {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) throw new RangeError(`${field} must be an ISO instant`);
  return ms;
}

/** The instant the Service is expected to end, as an ISO string. */
export function expectedServiceEnd(facts: ServiceTimingFacts, nowIso: string): string {
  if (!Number.isFinite(facts.durationMinutes) || facts.durationMinutes <= 0) {
    throw new RangeError("durationMinutes must be a positive number");
  }
  const now = instant(nowIso, "now");
  const durationMs = facts.durationMinutes * MINUTE_MS;

  // 1. Running: the server timer is the fact.
  if (facts.serviceEndsAt !== null) {
    return new Date(instant(facts.serviceEndsAt, "serviceEndsAt")).toISOString();
  }

  // 2. At the client, countdown running: auto start at the deadline.
  if (facts.serviceStartDeadlineAt !== null) {
    const start = Math.max(instant(facts.serviceStartDeadlineAt, "serviceStartDeadlineAt"), now);
    return new Date(start + durationMs).toISOString();
  }

  const scheduled = instant(facts.scheduledAt, "scheduledAt");

  // 4. Start still ahead: the confirmed window.
  if (scheduled > now) return new Date(scheduled + durationMs).toISOString();

  // 3. Start passed, not at the client: the earliest she could finish.
  return new Date(now + SERVICE_START_COUNTDOWN_MINUTES * MINUTE_MS + durationMs).toISOString();
}
